/**
 * Resource Timeline Widget
 * Shows operations scheduled per work center in a timeline view
 */

import { useState, useRef, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RefreshCw, AlertTriangle, Calendar, Eye, EyeOff } from 'lucide-react';
import clsx from 'clsx';
import type {
  GanttResponse,
  GanttOperation,
  GanttResource,
} from '../../hooks/useGanttData';
import {
  getOperationColor,
  calculateDateRange,
  generateDateLabels,
  calculateOperationPosition,
} from '../../hooks/useGanttData';

type ViewMode = 'day' | 'week' | 'month';

interface ResourceTimelineWidgetProps {
  data?: GanttResponse;
  loading?: boolean;
  error?: Error | null;
  onRefresh?: () => void;
  onOperationClick?: (op: GanttOperation) => void;
  onSelectScenario?: () => void;
  scenarioId?: string;
}

// Day cell width by view mode
const CELL_WIDTHS: Record<ViewMode, number> = {
  day: 80,
  week: 40,
  month: 20,
};

const ROW_HEIGHT = 48;

export function ResourceTimelineWidget({
  data,
  loading,
  error,
  onRefresh,
  onOperationClick,
  onSelectScenario,
  scenarioId,
}: ResourceTimelineWidgetProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('day');
  const [hoveredOp, setHoveredOp] = useState<GanttOperation | null>(null);
  const [showOriginalPlan, setShowOriginalPlan] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Memoize date range calculation
  const dateRange = useMemo(() => {
    if (!data || data.operations.length === 0) {
      const now = new Date();
      return {
        start: now,
        end: new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000),
      };
    }
    return calculateDateRange(data.operations);
  }, [data]);

  // Generate date labels based on view mode
  const dateLabels = useMemo(() => {
    return generateDateLabels(dateRange.start, dateRange.end, viewMode);
  }, [dateRange, viewMode]);

  // Calculate total timeline width
  const timelineWidth = useMemo(() => {
    return dateLabels.length * CELL_WIDTHS[viewMode];
  }, [dateLabels, viewMode]);

  // Group operations by work center
  const operationsByWorkCenter = useMemo(() => {
    if (!data) return new Map<string, GanttOperation[]>();

    const grouped = new Map<string, GanttOperation[]>();
    for (const op of data.operations) {
      const key = op.work_center_code || 'Unassigned';
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(op);
    }
    return grouped;
  }, [data]);

  // Get resources list
  const resources = useMemo(() => {
    if (!data) return [];
    return data.resources;
  }, [data]);

  // Scroll handlers
  const scrollLeft = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -CELL_WIDTHS[viewMode] * 3,
        behavior: 'smooth',
      });
    }
  }, [viewMode]);

  const scrollRight = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: CELL_WIDTHS[viewMode] * 3,
        behavior: 'smooth',
      });
    }
  }, [viewMode]);

  // Zoom handlers
  const zoomIn = useCallback(() => {
    if (viewMode === 'month') setViewMode('week');
    else if (viewMode === 'week') setViewMode('day');
  }, [viewMode]);

  const zoomOut = useCallback(() => {
    if (viewMode === 'day') setViewMode('week');
    else if (viewMode === 'week') setViewMode('month');
  }, [viewMode]);

  // No scenario selected
  if (!scenarioId) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-gray-500">
        <Calendar className="w-12 h-12 mb-4 text-gray-300" />
        <p className="text-sm mb-3">No Scenario Selected</p>
        {onSelectScenario && (
          <button
            onClick={onSelectScenario}
            className="px-4 py-2 text-sm bg-[#45bdd6] text-white rounded hover:bg-[#3aa8c0] transition-colors"
          >
            Select Scenario
          </button>
        )}
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#45bdd6] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Loading Timeline...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-red-500">
        <AlertTriangle className="w-10 h-10 mb-3" />
        <p className="text-sm text-center mb-3">{error.message || 'Error Loading'}</p>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="px-4 py-2 text-sm border border-red-300 rounded hover:bg-red-50"
          >
            Try Again
          </button>
        )}
      </div>
    );
  }

  // No data
  if (!data || data.operations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-gray-500">
        <Calendar className="w-10 h-10 mb-3 text-gray-300" />
        <p className="text-sm">No Operations Scheduled</p>
        <p className="text-xs text-gray-400 mt-1">
          Scenario: {data?.scenario_name || 'Unknown'}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header with controls */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">
            {data.scenario_name}
          </span>
          <span className="text-xs text-gray-500">
            ({data.total_operations} Operations)
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* View mode buttons */}
          <div className="flex rounded-md border border-gray-300 overflow-hidden">
            {(['day', 'week', 'month'] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={clsx(
                  'px-3 py-1 text-xs',
                  viewMode === mode
                    ? 'bg-[#45bdd6] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                )}
              >
                {mode === 'day' ? 'Day' : mode === 'week' ? 'Week' : 'Month'}
              </button>
            ))}
          </div>

          {/* Zoom controls */}
          <button
            onClick={zoomIn}
            disabled={viewMode === 'day'}
            className="p-1.5 text-gray-500 hover:text-gray-700 disabled:opacity-30"
            title="Zoom In"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            onClick={zoomOut}
            disabled={viewMode === 'month'}
            className="p-1.5 text-gray-500 hover:text-gray-700 disabled:opacity-30"
            title="Zoom Out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>

          {/* Toggle original plan overlay */}
          <button
            onClick={() => setShowOriginalPlan(!showOriginalPlan)}
            className={clsx(
              'flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors',
              showOriginalPlan
                ? 'bg-amber-50 border-amber-200 text-amber-700'
                : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
            )}
            title={showOriginalPlan ? 'Hide original ERP plan' : 'Show original ERP plan'}
          >
            {showOriginalPlan ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            ERP Plan
          </button>

          {/* Refresh */}
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-1.5 text-gray-500 hover:text-gray-700"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Legend bar */}
      {showOriginalPlan && (
        <div className="flex items-center gap-4 px-4 py-1.5 bg-gray-50 border-b text-[10px] text-gray-500">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-2.5 rounded border border-dashed border-gray-400 bg-gray-200/30" />
            <span>Original ERP Plan</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-2.5 rounded bg-blue-100 border-l-2 border-blue-500" />
            <span>AI Plan</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-green-500" />
            <span>Earlier</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-red-500" />
            <span>Later</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-amber-500" />
            <span>WC changed</span>
          </div>
        </div>
      )}

      {/* Timeline container */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Scroll left button */}
        <button
          onClick={scrollLeft}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-1 bg-white/90 border shadow-sm rounded-r hover:bg-gray-50"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>

        {/* Scroll right button */}
        <button
          onClick={scrollRight}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-1 bg-white/90 border shadow-sm rounded-l hover:bg-gray-50"
        >
          <ChevronRight className="w-4 h-4 text-gray-600" />
        </button>

        {/* Fixed resource column */}
        <div className="flex-shrink-0 w-40 bg-white border-r z-10">
          {/* Header cell */}
          <div className="h-10 flex items-center px-3 bg-gray-100 border-b font-medium text-xs text-gray-600">
            Work Center
          </div>

          {/* Resource rows */}
          {resources.map((resource) => (
            <div
              key={resource.code}
              className="flex items-center px-3 border-b bg-white"
              style={{ height: ROW_HEIGHT }}
            >
              <div className="truncate">
                <div className="text-sm font-medium text-gray-800 truncate">
                  {resource.code}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {resource.name}
                </div>
              </div>
            </div>
          ))}

          {/* Unassigned row if needed */}
          {operationsByWorkCenter.has('Unassigned') && (
            <div
              className="flex items-center px-3 border-b bg-gray-50"
              style={{ height: ROW_HEIGHT }}
            >
              <div className="text-sm text-gray-500 italic">Unassigned</div>
            </div>
          )}
        </div>

        {/* Scrollable timeline */}
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-x-auto overflow-y-auto"
        >
          <div style={{ width: timelineWidth, minWidth: '100%' }}>
            {/* Date header */}
            <div className="flex h-10 bg-gray-100 border-b sticky top-0 z-5">
              {dateLabels.map((label, i) => (
                <div
                  key={i}
                  className="flex-shrink-0 flex items-center justify-center border-r text-xs text-gray-600"
                  style={{ width: CELL_WIDTHS[viewMode] }}
                >
                  {label.label}
                </div>
              ))}
            </div>

            {/* Resource rows with operations */}
            {resources.map((resource) => {
              const ops = operationsByWorkCenter.get(resource.code) || [];
              return (
                <ResourceRow
                  key={resource.code}
                  resource={resource}
                  operations={ops}
                  dateRange={dateRange}
                  timelineWidth={timelineWidth}
                  viewMode={viewMode}
                  cellWidth={CELL_WIDTHS[viewMode]}
                  hoveredOp={hoveredOp}
                  onHover={setHoveredOp}
                  onClick={onOperationClick}
                  showOriginalPlan={showOriginalPlan}
                />
              );
            })}

            {/* Unassigned row */}
            {operationsByWorkCenter.has('Unassigned') && (
              <ResourceRow
                key="Unassigned"
                resource={{ code: 'Unassigned', name: 'Unassigned', capacity_hours_per_day: 0, group: '' }}
                operations={operationsByWorkCenter.get('Unassigned') || []}
                dateRange={dateRange}
                timelineWidth={timelineWidth}
                viewMode={viewMode}
                cellWidth={CELL_WIDTHS[viewMode]}
                hoveredOp={hoveredOp}
                onHover={setHoveredOp}
                onClick={onOperationClick}
                showOriginalPlan={showOriginalPlan}
              />
            )}
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {hoveredOp && (
        <OperationTooltip operation={hoveredOp} />
      )}
    </div>
  );
}

// Resource row component
interface ResourceRowProps {
  resource: GanttResource;
  operations: GanttOperation[];
  dateRange: { start: Date; end: Date };
  timelineWidth: number;
  viewMode: ViewMode;
  cellWidth: number;
  hoveredOp: GanttOperation | null;
  onHover: (op: GanttOperation | null) => void;
  onClick?: (op: GanttOperation) => void;
  showOriginalPlan?: boolean;
}

function ResourceRow({
  resource,
  operations,
  dateRange,
  timelineWidth,
  viewMode,
  cellWidth,
  hoveredOp,
  onHover,
  onClick,
  showOriginalPlan = false,
}: ResourceRowProps) {
  // Calculate grid lines
  const gridLines = useMemo(() => {
    const lines: number[] = [];
    let pos = 0;
    while (pos < timelineWidth) {
      lines.push(pos);
      pos += cellWidth;
    }
    return lines;
  }, [timelineWidth, cellWidth]);

  return (
    <div
      className="relative border-b bg-white"
      style={{ height: ROW_HEIGHT }}
    >
      {/* Grid lines */}
      {gridLines.map((pos, i) => (
        <div
          key={i}
          className="absolute top-0 bottom-0 border-r border-gray-100"
          style={{ left: pos }}
        />
      ))}

      {/* Ghost bars: Original ERP plan as dashed outlines */}
      {showOriginalPlan && operations.map((op) => {
        if (!op.erp_planned_start || !op.erp_planned_end) return null;

        // Create a ghost operation with original ERP dates
        const ghostOp: GanttOperation = {
          ...op,
          planned_start: op.erp_planned_start,
          planned_end: op.erp_planned_end,
        };
        const { left: ghostLeft, width: ghostWidth } = calculateOperationPosition(
          ghostOp,
          dateRange.start,
          dateRange.end,
          timelineWidth
        );

        return (
          <div
            key={`ghost-${op.id}`}
            className="absolute top-2 bottom-2 rounded border border-dashed border-gray-400/50 bg-gray-200/20"
            style={{
              left: Math.max(ghostLeft, 0),
              width: Math.max(ghostWidth, 4),
              zIndex: 1,
            }}
          >
            {ghostWidth > 40 && (
              <div className="px-1 py-0.5">
                <div className="text-[9px] text-gray-400 truncate">
                  ERP
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Operations (AI plan) */}
      {operations.map((op) => {
        const { left, width } = calculateOperationPosition(
          op,
          dateRange.start,
          dateRange.end,
          timelineWidth
        );

        const colors = getOperationColor(op);
        const isHovered = hoveredOp?.id === op.id;

        // Delta indicator color
        const deltaHours = op.start_delta_hours;
        const hasDelta = deltaHours !== null && deltaHours !== 0;

        return (
          <div
            key={op.id}
            className={clsx(
              'absolute top-1.5 bottom-1.5 rounded border-l-2 cursor-pointer transition-all',
              colors.bg,
              colors.border,
              isHovered ? 'shadow-md scale-[1.02] z-10' : 'shadow-sm'
            )}
            style={{
              left: Math.max(left, 0),
              width: Math.max(width, 4),
              zIndex: isHovered ? 10 : 2,
            }}
            onMouseEnter={() => onHover(op)}
            onMouseLeave={() => onHover(null)}
            onClick={() => onClick?.(op)}
          >
            {width > 60 && (
              <div className="px-1.5 py-0.5 overflow-hidden">
                <div className={clsx('text-xs font-medium truncate', colors.text)}>
                  {op.order_number}
                </div>
                {width > 100 && (
                  <div className="text-[10px] text-gray-500 truncate">
                    Op {op.operation_sequence}
                  </div>
                )}
              </div>
            )}

            {/* Delta indicator badge */}
            {hasDelta && (
              <div
                className={clsx(
                  'absolute -bottom-1 left-1 px-1 py-0 text-[8px] font-medium rounded-sm',
                  deltaHours! < 0 ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                )}
                style={{ zIndex: 3 }}
              >
                {deltaHours! < 0 ? '' : '+'}{deltaHours!.toFixed(0)}h
              </div>
            )}

            {/* Conflict indicator */}
            {op.has_conflicts && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full flex items-center justify-center" style={{ zIndex: 4 }}>
                <AlertTriangle className="w-2 h-2 text-white" />
              </div>
            )}

            {/* Work center changed indicator */}
            {op.work_center_changed && (
              <div
                className="absolute -top-1 -left-1 w-3 h-3 bg-amber-500 rounded-full"
                title={`WC changed from ${op.original_work_center_code}`}
                style={{ zIndex: 4 }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// Operation tooltip component
interface OperationTooltipProps {
  operation: GanttOperation;
}

function OperationTooltip({ operation: op }: OperationTooltipProps) {
  const colors = getOperationColor(op);

  return (
    <div className="fixed bottom-4 right-4 bg-white border shadow-lg rounded-lg p-3 z-50 w-72">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="font-medium text-gray-900">{op.order_number}</div>
          <div className="text-xs text-gray-500">Operation {op.operation_sequence}</div>
        </div>
        <span
          className={clsx(
            'px-2 py-0.5 text-xs rounded',
            colors.bg,
            colors.text
          )}
        >
          {op.approval_status}
        </span>
      </div>

      <div className="text-sm text-gray-600 mb-2 truncate">{op.item_description}</div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="text-gray-500">Start:</span>
          <span className="ml-1 text-gray-700">
            {new Date(op.planned_start).toLocaleString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
        <div>
          <span className="text-gray-500">End:</span>
          <span className="ml-1 text-gray-700">
            {new Date(op.planned_end).toLocaleString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
        <div>
          <span className="text-gray-500">Duration:</span>
          <span className="ml-1 text-gray-700">{op.duration_hours.toFixed(1)}h</span>
        </div>
        <div>
          <span className="text-gray-500">Customer:</span>
          <span className="ml-1 text-gray-700 truncate">{op.customer || '—'}</span>
        </div>
      </div>

      {op.has_conflicts && (
        <div className="mt-2 flex items-center gap-1 text-xs text-red-600">
          <AlertTriangle className="w-3 h-3" />
          <span>Conflicts Present</span>
        </div>
      )}

      {/* Delta from original */}
      {op.start_delta_hours !== null && op.start_delta_hours !== 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <div className="text-[10px] font-medium text-gray-500 mb-1 uppercase tracking-wider">Change from ERP Plan</div>
          <div className="flex items-center gap-3">
            <div className="flex-1 relative h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-300" />
              <div
                className={clsx(
                  'absolute top-0 bottom-0 rounded-full',
                  op.start_delta_hours < 0 ? 'bg-green-400' : 'bg-red-400'
                )}
                style={{
                  ...(op.start_delta_hours < 0
                    ? { right: '50%', width: `${Math.min(Math.abs(op.start_delta_hours) / 48 * 50, 50)}%` }
                    : { left: '50%', width: `${Math.min(op.start_delta_hours / 48 * 50, 50)}%` }),
                }}
              />
            </div>
            <span className={clsx(
              'text-xs font-medium whitespace-nowrap',
              op.start_delta_hours < 0 ? 'text-green-600' : 'text-red-600'
            )}>
              {op.start_delta_hours < 0 ? '' : '+'}{op.start_delta_hours.toFixed(1)}h
            </span>
          </div>
          {op.erp_planned_start && (
            <div className="text-[10px] text-gray-400 mt-1">
              Original: {new Date(op.erp_planned_start).toLocaleString('en-GB', {
                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
              })}
            </div>
          )}
        </div>
      )}

      {op.work_center_changed && (
        <div className="mt-1 flex items-center gap-1 text-xs text-amber-600">
          <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
          WC changed from {op.original_work_center_code}
        </div>
      )}
    </div>
  );
}

export default ResourceTimelineWidget;
